import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, Flexer } from '@ontruck/orc';
import cx from 'classnames';
import Tag from 'components/Tag';
import { formatters as dateFormatters } from 'utils/dateAndTimes';
import { SUGGESTION_TYPES } from 'state/direct-order/suggested-quotes/constants';
import LiteralCurrency from 'components/LiteralCurrency';
import { useDispatch, useSelector } from 'react-redux';
import {
  actions as analyticsActions,
  constants as analyticsEvents,
} from 'state/analytics';
import {
  actions as orderActions,
  selectors as orderSelectors,
} from 'state/order';
import { useTranslation } from 'react-i18next';
import { debounce } from 'throttle-debounce';
import useGetCurrentQuote from 'hooks/useGetCurrentQuote';
import { selectors as ordersSelectors } from 'state/orders';
import { actions as shipmentActions } from 'state/shipments';
import { constants as orderConstants } from 'utils/order';
import isDateToday from 'utils/dateAndTimes/methods/isDateToday';

const sendPriceShownEvent = debounce(
  0,
  ({ dispatch, type, mainQuoteRequest, quote, quoteRequest }) => {
    dispatch(
      analyticsActions.sendEvent(
        analyticsEvents.suggestedPriceShown({
          type,
          mainQuoteRequest,
          quote,
          quoteRequest,
        })
      )
    );
  }
);

const Suggestion = ({ type, mainQuoteRequest, quote, quoteRequest }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('Quote');
  useEffect(() => {
    sendPriceShownEvent({
      dispatch,
      type,
      mainQuoteRequest,
      quote,
      quoteRequest,
    });
  }, [type, mainQuoteRequest, quote, quoteRequest, dispatch]);

  const onClick = useCallback(() => {
    dispatch(orderActions.prefillSuggestedOrder({ type }));
    dispatch(orderActions.cleanQuoteSuggestions());
    dispatch(
      analyticsActions.sendEvent(
        analyticsEvents.suggestedPriceClicked({
          type,
          mainQuoteRequest,
          quote,
          quoteRequest,
        })
      )
    );
  }, [dispatch, mainQuoteRequest, quote, quoteRequest, type]);

  const shipment = quoteRequest?.shipments[0];
  const typeForLabel = type.toLowerCase();
  const pickupDate = dateFormatters.getHumanReadableFormattedDate({
    value: shipment?.pickup_date_from,
    format: 'friendly',
    t,
  });
  const dropoffDate = dateFormatters.getHumanReadableFormattedDate({
    value: shipment?.dropoff_date_from,
    format: 'friendly',
    t,
  });
  return (
    <Flexer.Child
      key={type}
      data-testid="price-suggest"
      className="price-suggest__suggestion"
      flexGrow="1"
      onClick={() => onClick(type, quote, quoteRequest)}
    >
      <div
        data-testid={'price-suggest_' + type}
        className={cx(
          'price-suggest__suggestion-header',
          `price-suggest__suggestion-${type}`
        )}
      >
        <div>
          <span className="price-suggest__suggestion-title">
            {t(`suggestions.service-title`)}
          </span>
          <Tag label={t(`suggestions.${typeForLabel}-tag`)} />
        </div>
        <div className="price-suggest__suggestion-price">
          <LiteralCurrency value={Number(quote?.net_full_price)} />
        </div>
      </div>

      <div className="price-suggest__suggestion-concept">
        {type === SUGGESTION_TYPES.TWENTY_FOUR_HOUR ? (
          <>
            <div>
              {t('suggestions.pickup')}: <span>{pickupDate}</span>
            </div>
            <div>
              {t('suggestions.dropoff')}: <span>{dropoffDate}</span>
            </div>
          </>
        ) : (
          <div>
            {t('suggestions.pickup-dropoff')}: <span>{pickupDate}</span>
          </div>
        )}
      </div>

      <div>
        <Button
          className="price-suggest__suggestion-btn"
          nude
          size="XS"
          icon="edit"
          iconPosition="left"
        >
          <span>{t('suggestions.change_time')}</span>
        </Button>
      </div>
    </Flexer.Child>
  );
};

const suggestionsHiddenInDomestic = [
  SUGGESTION_TYPES.DIRECT,
  SUGGESTION_TYPES.TODAY,
];

const SuggestEconomy = () => {
  const { t } = useTranslation('Quote');
  const dispatch = useDispatch();
  const type = 'ECONOMY';
  const isFetching = useSelector(ordersSelectors.isFetching);
  const quote = useGetCurrentQuote();
  let canBeEconomy = false;

  if (
    quote.available_products !== undefined &&
    quote.available_products.incompatible.length > 0
  ) {
    const incompatibleProducts = quote.available_products.incompatible;
    canBeEconomy =
      incompatibleProducts.filter(
        (product) =>
          product.product_type === 'ECONOMY' &&
          product.info.some((e) => e.reason === 'TIME_RESTRICTIONS')
      ).length === 1;
  }
  let isEconomy = quote.product_type === 'ECONOMY';

  const orderType = useSelector(orderSelectors.getTypeOfOrder);
  const isMultiroute =
    [orderConstants.COLLECTION, orderConstants.DISTRIBUTION].indexOf(
      orderType
    ) >= 0;

  let isForToday = false;
  const shipmentsWithLoads = useSelector(
    orderSelectors.getShipmentsWithLoadsInForm
  );
  const datesToCheck = [];
  Object.keys(shipmentsWithLoads).forEach((key) => {
    datesToCheck.push(shipmentsWithLoads[key].dropoff_date_from);
    datesToCheck.push(shipmentsWithLoads[key].dropoff_date_to);
    datesToCheck.push(shipmentsWithLoads[key].pickup_date_from);
    datesToCheck.push(shipmentsWithLoads[key].pickup_date_to);
  });
  if (datesToCheck.some((date) => isDateToday(date))) isForToday = true;

  const showSuggestEconomy =
    !isFetching && canBeEconomy && !isEconomy && !isMultiroute && !isForToday;

  const shipmentKeys = useSelector((state) =>
    orderSelectors.getShipmentKeys(state)
  );
  const shipmentKey = shipmentKeys[0];

  if (!showSuggestEconomy) return <></>;

  const convertToEconomy = () => {
    // wil fail for validation
    dispatch(
      shipmentActions.selectTime({
        stopType: 'pickup',
        timeParam: 'time_from',
        value: '08:00',
        shipmentKey,
        diffDay: 0,
      })
    );
    // wil fail for validation
    dispatch(
      shipmentActions.selectTime({
        stopType: 'pickup',
        timeParam: 'time_to',
        value: '18:00',
        shipmentKey,
        diffDay: 0,
      })
    );
    // wil fail for validation
    dispatch(
      shipmentActions.selectTime({
        stopType: 'dropoff',
        timeParam: 'time_from',
        value: '08:00',
        shipmentKey,
        diffDay: 0,
      })
    );
    // wil fail for validation
    dispatch(
      shipmentActions.selectTime({
        stopType: 'dropoff',
        timeParam: 'time_to',
        value: '18:00',
        shipmentKey,
        diffDay: 0,
      })
    );
    // enableEconomyMode to disable validation for times
    dispatch(orderActions.enableEconomyMode());
    // fetch next quote (will be economy)
    dispatch(orderActions.fetchQuote());
  };
  return (
    // same styles that suggestion
    <>
      <Flexer.Child
        key={type}
        data-testid="price-suggest"
        className="price-suggest__suggestion"
        flexGrow="1"
        onClick={convertToEconomy}
      >
        <div
          data-testid={'price-suggest_' + type}
          className={cx(
            'price-suggest__suggestion-header',
            `price-suggest__suggestion-${type}`
          )}
        >
          <div>
            <span className="price-suggest__suggestion-title">
              {t(`extras.ONTRUCK_ECONOMY_DISCOUNT.label`)}
            </span>
          </div>
          <div className="price-suggest__suggestion-price">
            <Tag label={t(`extras.ONTRUCK_ECONOMY_DISCOUNT.new_service`)} />
          </div>
        </div>

        <div>
          <p className="text-align-left">
            {t(`extras.ONTRUCK_ECONOMY_DISCOUNT.details`)}
          </p>
          <Button
            className={`price-suggest__suggestion-btn price-suggest__suggestion-btn-${type}`}
            nude
            size="XS"
            icon="time"
            iconPosition="left"
          >
            <span>{t(`extras.ONTRUCK_ECONOMY_DISCOUNT.action`)}</span>
          </Button>
        </div>
      </Flexer.Child>
    </>
  );
};

const PriceSuggest = ({ className, showNonFeasibleEstimationMessage }) => {
  const suggestions = useSelector(orderSelectors.getQuoteSuggestions);
  const renderSuggestion = (
    { type, mainQuoteRequest, quote, quoteRequest },
    i
  ) => {
    if (quote.haul === 'long' && suggestionsHiddenInDomestic.includes(type)) {
      return null;
    }

    return (
      <Suggestion
        key={i}
        type={type}
        mainQuoteRequest={mainQuoteRequest}
        quote={quote}
        quoteRequest={quoteRequest}
      />
    );
  };
  const isEditing = useSelector(orderSelectors.getIsEditing);

  return (
    <div className={cx(className, 'price-suggest')}>
      <Flexer.Parent flexWrap="wrap">
        {!isEditing &&
          !showNonFeasibleEstimationMessage &&
          suggestions.map(renderSuggestion)}
        <SuggestEconomy />
      </Flexer.Parent>
    </div>
  );
};

PriceSuggest.propTypes = {
  className: PropTypes.string,
  showNonFeasibleEstimationMessage: PropTypes.bool,
};

export default PriceSuggest;
